/**
 * Subscription Context
 * Provides centralized subscription state management with smart caching
 *
 * COPIED FROM MOBILE - EXACT IMPLEMENTATION THAT WORKS
 */

import React, { createContext, useContext, useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { apiClient } from '@/shared/services/apiClient';
import { API_ROUTES } from '@/shared/services/apiRoutes';

// Cache configuration (industry standards)
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes - how long data is considered fresh
const STALE_TIME = 30 * 1000; // 30 seconds - show stale data while revalidating

export interface UsageData {
  subscribed: boolean;
  is_paused: boolean;
  isTrialActive: boolean;
  planType: string;
  subscriptionTier?: string | null;
  subscriptionEndDate: string | null;
  subscriptionStatus: string | null;
  subscription_status: string | null; // Legacy field
  daysLeftInTrial: number;
  billingCycleStart?: string | null;
  billingCycleEnd?: string | null;
  nextBillingDate: string | null;
  cancellationDate?: string | null;
  pausedAt?: string | null;
  resumesAt?: string | null;
  trialType: string | null;
  trial_end_date: string | null;
  currentUsers?: number;
  maxUsers?: number;
  currentStorageGB?: number;
  maxStorageGB?: number;
  currentRecordings?: number;
  maxRecordings?: number;
  retentionDays?: number;
  access_blocked?: boolean;
  storageWarning?: boolean;
  userWarning?: boolean;
  recordingWarning?: boolean;
  loading?: boolean;
  error?: string | null;

  // NEW: Usage tracking
  calls_count?: number;
  calls_limit?: number | null;
  canMakeCalls?: boolean;
  recordings_count?: number;
  recordings_limit?: number | null;
  canMakeRecordings?: boolean;

  // NEW: Pro trial tracking
  is_pro_trial?: boolean;
  pro_trial_end_date?: string | null;

  // NEW: Billing dates (both formats for compatibility)
  last_billing_date?: string | null;
  lastBillingDate?: string | null;
  next_billing_date?: string | null;
  billing_cycle_start?: string | null;

  // Invoice subscription fields (user-level add-on)
  has_invoice_subscription?: boolean;
  invoice_subscription_status?: string | null;
  invoice_subscription_start_date?: string | null;
  invoice_subscription_end_date?: string | null;
  invoice_stripe_subscription_id?: string | null;
  invoice_stripe_price_id?: string | null;

  // Trial eligibility for returning users
  hasUsedTrial?: boolean;
  canStartTrial?: boolean;
  has_had_subscription?: boolean;
  first_subscription_at?: string | null;

  // Addon display fields (derived from user_count, retention_days)
  extraUserCount?: number;
  storagePlan?: 'base' | '90_days' | '1_year';

  [key: string]: any; // Allow additional properties
}

const DEFAULT_USAGE: UsageData = {
  subscribed: false,
  is_paused: false,
  isTrialActive: false,
  planType: 'free',
  subscriptionTier: null,
  subscriptionEndDate: null,
  subscriptionStatus: null,
  subscription_status: null,
  daysLeftInTrial: 0,
  billingCycleStart: null,
  billingCycleEnd: null,
  nextBillingDate: null,
  cancellationDate: null,
  pausedAt: null,
  resumesAt: null,
  trialType: null,
  trial_end_date: null,
  currentUsers: 0,
  maxUsers: 1,
  currentStorageGB: 0,
  maxStorageGB: 5,
  currentRecordings: 0,
  maxRecordings: 3,
  retentionDays: 30,
  access_blocked: false,
  storageWarning: false,
  userWarning: false,
  recordingWarning: false,
  loading: false,
  error: null,

  // NEW: Usage tracking
  calls_count: 0,
  calls_limit: 10, // Default for free trial
  canMakeCalls: true,
  recordings_count: 0,
  recordings_limit: 3, // Default for free trial
  canMakeRecordings: true,

  // NEW: Pro trial
  is_pro_trial: false,
  pro_trial_end_date: null,

  // NEW: Billing dates
  last_billing_date: null,
  lastBillingDate: null,
  next_billing_date: null,
  billing_cycle_start: null,

  // Invoice subscription
  has_invoice_subscription: false,
  invoice_subscription_status: 'none',
  invoice_subscription_start_date: null,
  invoice_subscription_end_date: null,
  invoice_stripe_subscription_id: null,
  invoice_stripe_price_id: null,

  // Trial eligibility
  hasUsedTrial: false,
  canStartTrial: true,
  has_had_subscription: false,
  first_subscription_at: null,

  // Addon display
  extraUserCount: 0,
  storagePlan: 'base',
};

interface SubscriptionContextValue {
  usage: UsageData;
  loading: boolean;
  error: string | null;
  isAccessBlocked: boolean;
  refreshSubscription: () => Promise<void>;
  lastFetchTime: number;
  isCacheStale: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [usage, setUsage] = useState<UsageData>(DEFAULT_USAGE);
  const [loading, setLoading] = useState(true); // Start with true to prevent FOIC
  const [error, setError] = useState<string | null>(null);
  const lastFetchTimeRef = useRef<number>(0);
  const hasInitializedRef = useRef<boolean>(false);
  const fetchingRef = useRef<boolean>(false); // Prevent overlapping fetches
  const { user, isLoading: authLoading, logout } = useAuth(); // userDataLoaded removed - not needed

  /**
   * Fetch subscription data with smart caching
   */
  const fetchSubscription = useCallback(async (forceRefresh = false) => {
    // CRITICAL FIX: Prevent overlapping fetches
    if (fetchingRef.current && !forceRefresh) {
      console.log('[SubscriptionContext] Fetch already in progress, skipping...');
      return;
    }

    // CRITICAL FIX: Wait for auth to finish loading before checking user
    if (authLoading) {
      console.log('[SubscriptionContext] Auth still loading, waiting...');
      return;
    }

    // CRITICAL FIX: Backend returns user_id, not id - check both for compatibility
    const userId = (user as any)?.id || (user as any)?.user_id;

    if (!userId) {
      // Silent for public/guest users - prevents console spam and error flashes
      setUsage(DEFAULT_USAGE);
      setLoading(false);
      return;
    }

    // CRITICAL FIX: Use localStorage instead of getSession() to prevent hanging
    // Skip session check if forcing refresh or if we have a recent successful fetch
    if (!forceRefresh && lastFetchTimeRef.current === 0) {
      try {
        // Check localStorage for session instead of calling getSession()
        const { hasSession } = await import('@/shared/utils/supabaseStorage');

        if (!hasSession()) {
          console.warn('[SubscriptionContext] No session in localStorage yet, will retry');
          setLoading(false);
          return;
        }
      } catch (sessionError: any) {
        // If session check fails, proceed anyway - let API handle auth
        console.warn('[SubscriptionContext] Session check failed, proceeding with API call:', sessionError.message);
      }
    }

    console.log('[SubscriptionContext] fetchSubscription called with user:', {
      userId: userId,
      forceRefresh,
      lastFetchTime: lastFetchTimeRef.current,
      authLoading
    });

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    // Skip if data is fresh (within CACHE_TTL) and not forcing refresh
    if (!forceRefresh && timeSinceLastFetch < CACHE_TTL && lastFetchTimeRef.current > 0) {
      console.log('[SubscriptionContext] Using cached data (fresh)');
      return;
    }

    // Mark as fetching
    fetchingRef.current = true;

    // Stale-while-revalidate: Show old data while fetching new
    // ✅ FIX: Only show loading on initial fetch (never on background revalidation)
    // This prevents the loading screen from unmounting the dashboard during refresh
    const shouldShowLoading = lastFetchTimeRef.current === 0;

    if (shouldShowLoading) {
      console.log('[SubscriptionContext] Initial fetch - showing loading');
      setLoading(true);
    } else {
      console.log('[SubscriptionContext] Revalidating in background (stale data visible)');
    }

    try {
      console.log('[SubscriptionContext] Calling API:', API_ROUTES.BILLING_USAGE);
      const response = await apiClient.get<{ ok: boolean; usage: UsageData }>(API_ROUTES.BILLING_USAGE);

      console.log('[SubscriptionContext] API Response:', response);

      if (response.ok && response.usage) {
        // DEBUG: Log critical subscription fields
        console.log('[SubscriptionContext] Critical fields:', {
          subscribed: response.usage.subscribed,
          subscription_status: response.usage.subscription_status,
          subscriptionStatus: response.usage.subscriptionStatus,
          isTrialActive: response.usage.isTrialActive,
          planType: response.usage.planType,
          subscriptionTier: response.usage.subscriptionTier
        });

        setUsage(response.usage);
        setError(null);
        lastFetchTimeRef.current = now;
        console.log('[SubscriptionContext] Subscription data updated:', response.usage);
      } else {
        throw new Error('Invalid response from billing API');
      }
    } catch (err: any) {
      console.error('[SubscriptionContext] Error fetching subscription:', err);
      setError(err.message || 'Failed to load subscription');

      // CRITICAL FIX: Redirect to landing page instead of showing fake data
      if (lastFetchTimeRef.current === 0) {
        console.error('[SubscriptionContext] Initial subscription fetch failed - redirecting to landing page');
        // Sign out user and redirect to landing page
        await logout();
        window.location.href = '/';
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false; // Always reset fetching flag
    }
  }, [(user as any)?.id, (user as any)?.user_id, authLoading, logout]); // FIXED: Removed userDataLoaded - causes infinite loop

  /**
   * Check if cached data is stale
   */
  const isCacheStaleValue = useCallback(() => {
    if (lastFetchTimeRef.current === 0) return true;
    const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
    return timeSinceLastFetch > STALE_TIME;
  }, []);

  /**
   * OPTIMIZED: Single fetch trigger - start immediately when auth session exists
   * No longer waits for userDataLoaded - can run in parallel with user data fetch
   */
  useEffect(() => {
    const userId = (user as any)?.id || (user as any)?.user_id;

    console.log('[SubscriptionContext] useEffect triggered:', {
      hasUser: !!user,
      userId: userId,
      authLoading,
      hasInitialized: hasInitializedRef.current
    });

    if (userId && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setLoading(true);
      console.log('[SubscriptionContext] 🚀 Starting initial subscription fetch for user:', userId);

      // CRITICAL FIX: Add error handling to prevent infinite loading
      fetchSubscription(true).catch(async (err) => {
        console.error('[SubscriptionContext] ❌ Initial fetch failed:', err);
        // Ensure loading state is reset even on error
        setLoading(false);
        fetchingRef.current = false;
        // CRITICAL FIX: Redirect to landing page instead of showing fake data
        console.error('[SubscriptionContext] Initial subscription fetch failed - redirecting to landing page');
        await logout();
        window.location.href = '/';
      });
    } else if (!userId && hasInitializedRef.current) {
      console.log('[SubscriptionContext] User logged out, resetting subscription state');
      hasInitializedRef.current = false;
      setUsage(DEFAULT_USAGE);
      setLoading(false);
    } else if (!userId && !authLoading) {
      // CRITICAL FIX: If no user and auth is done loading, immediately set loading to false
      console.log('[SubscriptionContext] No user detected, clearing loading state');
      setLoading(false);
    }
  }, [(user as any)?.id, (user as any)?.user_id, authLoading, logout])

  /**
   * CRITICAL SAFETY: Timeout protection - ensure loading never stays true indefinitely
   */
  useEffect(() => {
    if (!loading) return;

    const timeout = setTimeout(() => {
      if (loading) {
        console.error('[SubscriptionContext] ⏱️ TIMEOUT: Loading state stuck, forcing to false');
        setLoading(false);
        fetchingRef.current = false;
      }
    }, 10000); // 10 second safety timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  /**
   * OPTIMIZED: Background revalidation every 30 seconds
   * Only polls if user exists and cache is stale
   * PRODUCTION FIX: Use ref to store latest fetchSubscription to avoid dependency loop
   */
  const fetchSubscriptionRef = useRef(fetchSubscription);
  useEffect(() => {
    fetchSubscriptionRef.current = fetchSubscription;
  }, [fetchSubscription]);

  useEffect(() => {
    const userId = (user as any)?.id || (user as any)?.user_id;
    if (!userId) return;

    // CRITICAL FIX: Align interval staleness check with fetchSubscription's CACHE_TTL logic
    // Check frequently (30s) but only refetch when cache is actually stale (5min+)
    const interval = setInterval(() => {
      const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
      const isStale = lastFetchTimeRef.current === 0 || timeSinceLastFetch >= CACHE_TTL;

      if (isStale) {
        console.log('[SubscriptionContext] Cache stale (5min+), revalidating...');
        fetchSubscriptionRef.current(false); // fetchSubscription handles its own fresh check
      }
    }, STALE_TIME); // Check every 30s for quick invalidation detection

    return () => clearInterval(interval);
  }, [(user as any)?.id, (user as any)?.user_id]); // STABLE: Only depends on userId

  /**
   * Public refresh function
   */
  const refreshSubscription = useCallback(async () => {
    await fetchSubscription(true);
  }, [fetchSubscription]);

  const value: SubscriptionContextValue = useMemo(() => ({
    usage,
    loading,
    error,
    isAccessBlocked: usage.access_blocked || false,
    refreshSubscription,
    lastFetchTime: lastFetchTimeRef.current,
    isCacheStale: isCacheStaleValue(),
  }), [usage, loading, error, refreshSubscription, isCacheStaleValue]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 */
export function useSubscription(): SubscriptionContextValue {
  const context = useContext(SubscriptionContext);

  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }

  return context;
}

// Export both names for compatibility
export const useSubscriptionContext = useSubscription;
